import React, { useEffect, useState } from "react";
import { TrendingUp, Wallet, FileText, Clock, X, ChevronRight, Users } from "lucide-react";
import { api } from "../../lib/api";
import { formatINR } from "../../lib/format";

function MiniBar({ data, color = "bg-[#F97316]", height = 80 }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data.map((d) => d.deals), 1);
  return (
    <div className="flex items-end gap-1.5" style={{ height }}>
      {data.map((d, i) => {
        const pct = Math.max((d.deals / max) * 100, d.deals > 0 ? 6 : 0);
        const isLast = i === data.length - 1;
        return (
          <div key={d.month} className="flex-1 flex flex-col items-center gap-1">
            {d.deals > 0 && <span className="text-[10px] text-gray-500">{d.deals}</span>}
            <div className="w-full flex items-end" style={{ height: height - 20 }}>
              <div
                className={`w-full rounded-t transition-all ${isLast ? color : color.replace("500", "200")}`}
                style={{ height: `${pct}%`, minHeight: d.deals > 0 ? 4 : 0 }}
              />
            </div>
            <span className="text-[10px] text-gray-400">{d.month}</span>
          </div>
        );
      })}
    </div>
  );
}

function StatCard({ label, value, sub, icon: Icon, color }) {
  const palette = {
    indigo: "bg-[#FFF4ED] text-[#F97316]",
    emerald: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    violet: "bg-[#FFF4ED] text-[#F97316]",
  };
  return (
    <div className="card-base p-5">
      <div className="flex items-center justify-between">
        <div className="text-xs text-gray-500 uppercase tracking-wider">{label}</div>
        <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${palette[color]}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className="mt-3 text-3xl font-bold font-display">{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
    </div>
  );
}

function AgentCard({ agent, rank, onClick, selected }) {
  const initials = agent.name?.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  const colors = ["bg-[#F97316]", "bg-[#F97316]", "bg-emerald-500", "bg-amber-500", "bg-pink-500", "bg-sky-500"];
  const bg = colors[rank % colors.length];

  return (
    <button
      onClick={onClick}
      className={`card-base p-5 text-left w-full transition-all hover:shadow-md ${selected ? "ring-2 ring-[#F97316] bg-[#FFF4ED]/30" : ""}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`h-10 w-10 rounded-full ${bg} text-white flex items-center justify-center font-semibold text-sm flex-shrink-0`}>
            {initials}
          </div>
          <div>
            <div className="font-semibold text-sm">{agent.name}</div>
            <div className="text-xs text-gray-500">{agent.phone}</div>
          </div>
        </div>
        <ChevronRight className={`h-4 w-4 text-gray-400 mt-1 transition-transform ${selected ? "rotate-90" : ""}`} />
      </div>

      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="text-center">
          <div className="text-xl font-bold font-display text-[#F97316]">{agent.deals_this_month}</div>
          <div className="text-[10px] text-gray-500 uppercase">This month</div>
        </div>
        <div className="text-center border-x border-gray-100">
          <div className="text-xl font-bold font-display">{agent.deals_total}</div>
          <div className="text-[10px] text-gray-500 uppercase">All time</div>
        </div>
        <div className="text-center">
          <div className="text-xl font-bold font-display text-emerald-600">{agent.active_rentals}</div>
          <div className="text-[10px] text-gray-500 uppercase">Active</div>
        </div>
      </div>

      <MiniBar data={agent.monthly_deals} color="bg-[#F97316]" height={60} />
    </button>
  );
}

function AgentPanel({ agent, onClose, rank }) {
  if (!agent) return null;
  const colors = ["bg-[#F97316]", "bg-[#F97316]", "bg-emerald-500", "bg-amber-500", "bg-pink-500", "bg-sky-500"];
  const bg = colors[rank % colors.length];
  const initials = agent.name?.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  const bestMonth = agent.monthly_deals ? Math.max(...agent.monthly_deals.map((m) => m.deals), 1) : 1;

  const stats = [
    { label: "Deals this month", value: agent.deals_this_month, color: "text-[#F97316]" },
    { label: "Total deals", value: agent.deals_total, color: "text-gray-900" },
    { label: "Active rentals", value: agent.active_rentals, color: "text-emerald-600" },
    { label: "Dalali recorded", value: formatINR(agent.brokerage_total_paise), color: "text-amber-600" },
  ];

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" onClick={onClose} />
      <div
        className="relative w-full max-w-md bg-white h-full shadow-2xl flex flex-col overflow-y-auto"
        style={{ animation: "slideInRight 0.25s ease-out" }}
        onClick={(e) => e.stopPropagation()}
      >
        <style>{`@keyframes slideInRight { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>

        <div className={`${bg} p-6 text-white flex-shrink-0`}>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="h-14 w-14 rounded-full bg-white/20 flex items-center justify-center text-xl font-bold">
                {initials}
              </div>
              <div>
                <div className="font-bold text-lg">{agent.name}</div>
                <div className="text-white/70 text-sm">{agent.phone}</div>
                <div className="mt-1.5">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-white/20">
                    {agent.kyc_status === "verified" ? "✓ KYC verified" : "KYC pending"}
                  </span>
                </div>
              </div>
            </div>
            <button onClick={onClose} className="text-white/70 hover:text-white p-1">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="mt-3 text-white/60 text-xs">Joined {agent.joined}</div>
        </div>

        <div className="p-5 grid grid-cols-2 gap-3">
          {stats.map((s) => (
            <div key={s.label} className="card-base p-4">
              <div className="text-xs text-gray-500 uppercase tracking-wide">{s.label}</div>
              <div className={`mt-1.5 text-2xl font-bold font-display ${s.color}`}>{s.value}</div>
            </div>
          ))}
        </div>

        <div className="px-5 pb-4">
          <div className="card-base p-4">
            <div className="font-semibold text-sm mb-1">Monthly deals — last 6 months</div>
            <div className="text-xs text-gray-500 mb-4">Rentals created per month</div>
            <MiniBar data={agent.monthly_deals} color="bg-[#F97316]" height={100} />
          </div>
        </div>

        <div className="px-5 pb-8">
          <div className="card-base p-4">
            <div className="font-semibold text-sm mb-3">Performance</div>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-xs mb-1.5">
                  <span className="text-gray-500">Active vs total deals</span>
                  <span className="font-medium">{agent.deals_total > 0 ? Math.round((agent.active_rentals / agent.deals_total) * 100) : 0}%</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${agent.deals_total > 0 ? Math.round((agent.active_rentals / agent.deals_total) * 100) : 0}%` }} />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-xs mb-1.5">
                  <span className="text-gray-500">This month vs best month</span>
                  <span className="font-medium">{Math.round((agent.deals_this_month / bestMonth) * 100)}%</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-[#F97316] rounded-full" style={{ width: `${Math.round((agent.deals_this_month / bestMonth) * 100)}%` }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedRank, setSelectedRank] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get("/analytics");
        setData(d);
      } catch (e) {
        setError(e.response?.data?.detail || "Failed to load analytics.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div>
        <h1 className="text-2xl font-bold font-display">Analytics</h1>
        <p className="text-sm text-gray-500 mt-1">Agency performance at a glance.</p>
      </div>

      {loading && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((n) => <div key={n} className="card-base p-5 h-28 animate-pulse bg-gray-50" />)}
        </div>
      )}

      {error && <div className="card-base p-6 text-center text-sm text-gray-500">{error}</div>}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Deals this month" value={data.deals_this_month} sub="rentals created this month" icon={TrendingUp} color="indigo" />
            <StatCard label="Total commission" value={formatINR(data.total_commission_paise)} sub="Orello commission all time" icon={Wallet} color="emerald" />
            <StatCard label="Active rentals" value={data.active_rentals} sub="deals running right now" icon={FileText} color="violet" />
            <StatCard label="Pending agreements" value={data.pending_agreements} sub="waiting to be signed" icon={Clock} color="amber" />
          </div>

          <div className="card-base p-5">
            <div className="mb-4">
              <h3 className="font-semibold">Agency deals — last 6 months</h3>
              <p className="text-xs text-gray-500 mt-0.5">Total rentals created across all agents</p>
            </div>
            <MiniBar data={data.monthly_deals} color="bg-[#F97316]" height={120} />
          </div>

          {data.agents && data.agents.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Users className="h-4 w-4 text-gray-400" />
                <h2 className="font-semibold">Agent performance</h2>
                <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{data.agents.length} agents</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {data.agents.map((agent, i) => (
                  <AgentCard
                    key={agent.id}
                    agent={agent}
                    rank={i}
                    selected={selectedAgent?.id === agent.id}
                    onClick={() => {
                      if (selectedAgent?.id === agent.id) { setSelectedAgent(null); }
                      else { setSelectedAgent(agent); setSelectedRank(i); }
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          {data.agents && data.agents.length === 0 && (
            <div className="card-base p-10 text-center">
              <Users className="h-10 w-10 mx-auto text-gray-200 mb-3" />
              <div className="text-sm text-gray-500">No agents on your team yet.</div>
              <div className="text-xs text-gray-400 mt-1">Add agents from the Team page to see their performance here.</div>
            </div>
          )}
        </>
      )}

      {selectedAgent && (
        <AgentPanel agent={selectedAgent} rank={selectedRank} onClose={() => setSelectedAgent(null)} />
      )}
    </div>
  );
}